<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import SnackBar from '@/components/UI/SnackBar.vue'
import { useMainStore } from '@/stores/MainStore'
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

const appUrl = import.meta.env.VITE_APP_URL

const mainStore = useMainStore()

const route = useRoute()

const showDrawer = ref(false)

const { isAuthenticated: userIsAuthenticated, isProspector: userIsProspector } = storeToRefs(mainStore)

// Watch the `showDrawer` ref to toggle scroll-lock on the body
watch(showDrawer, (isOpen) => {
  const body = document.body
  if (isOpen) {
    body.classList.add('scroll-lock')
  } else {
    body.classList.remove('scroll-lock')
  }
})

// Function to toggle the drawer
const toggleDrawer = () => {
  showDrawer.value = !showDrawer.value
}

const logout = async () => {
  try {
    // Clear app state, cookies, session storage, and local storage
    await mainStore.logout() // Clears app state and storage

    // Redirect directly to Azure Static Web App logout to invalidate session
    window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(appUrl)}`
  } catch (error) {
    console.error('Logout failed:', error)
  }
}
</script>

<template>
  <v-app>
    <v-app-bar>
      <!-- Left: Logo -->
      <router-link :to="{ name: 'home' }" aria-label="To the homepage">
        <img alt="Norhybrid logo" class="header__image" src="@/assets/norhybrid_logo.svg" />
      </router-link>

      <!-- Center: Enlarged Buttons -->
      <div class="App__centered-buttons">
        <!-- Projects Button -->
        <v-btn
          :to="{ name: 'projects' }"
          :class="{ 'App__active-button': route.path.startsWith('/projects') }"
          v-show="userIsAuthenticated"
        >
          <span>Projects</span>
        </v-btn>

        <!-- Prospects Button -->
        <v-btn
          :to="{ name: 'prospects' }"
          :class="{ 'App__active-button': route.path.startsWith('/prospects') }"
          v-show="userIsAuthenticated && userIsProspector"
        >
          <span>Prospecting</span>
        </v-btn>
      </div>

      <!-- Right: User Info and Drawer Icon -->
      <v-spacer></v-spacer>
      <div class="header__userinfo">
        <v-chip class="py-6">
          <span>
            <p>{{ mainStore.userName }}</p>
            <p class="font-italic text-grey-darken-2">{{ mainStore.orgName }}</p>
          </span>
        </v-chip>
      </div>
      <v-btn icon @click="toggleDrawer">
        <v-icon>mdi-menu</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Drawer -->
    <v-navigation-drawer
      v-model="showDrawer"
      disable-resize-watcher
      location="right"
      temporary
      scrim
    >
      <v-list class="drawer-list">
        <v-list-item :title="mainStore.userName">
          <span class="App__drawer-org-name">{{ mainStore.orgName }}</span>
        </v-list-item>
        <v-divider />
        <v-list-item @click="logout" class="logout-item">
          <v-list-item-title class="logout-title">
            <v-icon left class="mr-2">mdi-logout</v-icon>
            Logout
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <v-container>
        <RouterView />
        <SnackBar />
      </v-container>
    </v-main>
  </v-app>
</template>

<style lang="scss">
/*deprecation warning of imports css TODO: fix*/

.scroll-lock {
  overflow: hidden;
  height: 100%; /* Prevent vertical scrolling */
}

.App__centered-buttons {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 4px;
}

.App__drawer-org-name {
  font-size: inherit;
  letter-spacing: 0.05em;
  color: #888;
  font-style: italic;
  font-size: 0.75rem;
}

.header__image {
  padding-left: 60px;
  height: 50px;
  width: auto;

  @media (max-width: 959px) {
    padding-left: 16px;
  }

  @media (max-width: 660px) {
    display: none;
  }
}

.header__userinfo {
  height: 48px !important;
  margin-right: 4px;

  @media (max-width: 760px) {
    display: none;
  }
}

.drawer-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.logout-item {
  margin-top: auto;
}

.logout-title {
  text-align: center;
  width: 100%;
}
</style>
